import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormLabel, Row } from 'react-bootstrap';
import { selectedFieldSelector, updateSelectedField } from '../../../../../../slices/formadmin';
import { Option } from '../../../../../../models/form/option';
import { filter } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import CheckboxInput from '../../../../../../components/library/inputs/checkbox/CheckboxInput';
import { OptionListFieldModel } from '../../../../../../models/form/fields/optionList';
import { SelectFieldModel } from '../../../../../../models/form/fields/select';
import { FieldType } from '../../../../../../models/form/fieldType';
import { FieldModel } from '../../../../../../models/form/field';
import CultureTextInput from '../../../../../../components/culture/CultureTextInput';
import { RootState } from '../../../../../../store';

const OptionListOptions: FC = () => {
  const fieldTemp = useSelector(selectedFieldSelector) as FieldModel;
  const field =
    fieldTemp.type == FieldType.OptionList
      ? (useSelector(selectedFieldSelector) as OptionListFieldModel)
      : (useSelector(selectedFieldSelector) as SelectFieldModel);
  const dispatch = useDispatch();
  const intl = useIntl();
  const value: string | string[] | undefined = field.defaultValue;
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];

  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (field.options) {
      setOptions(field.options);
    }
  }, [field]);
  const updateOption = (option, index) => {
    const newOptions = [...options.slice(0, index), option, ...options.slice(index + 1)];
    setOptions(newOptions);
    dispatch(
      updateSelectedField({
        ...field,
        options: newOptions,
      }),
    );
  };

  const isDefaultValue = (option: string): boolean => {
    return !!(value && value.length > 0 && value.includes(option));
  };

  const toggleDefault = (option: string): void => {
    const isChecked = isDefaultValue(option);
    if (field.type == FieldType.OptionList) {
      if (field.multiSelection) {
        if (!field.defaultValue) {
          onChangeValue([option]);
        } else {
          if (isChecked) {
            onChangeValue(field.defaultValue.filter((val) => val !== option));
          } else {
            onChangeValue(field.defaultValue.concat(option));
          }
        }
      } else if (!field.multiSelection) {
        if (isChecked) {
          onChangeValue([]);
        } else {
          onChangeValue([option]);
        }
      }
    } else {
      if (isChecked) {
        onChangeValue([]);
      } else {
        onChangeValue([option]);
      }
    }
  };

  const onChangeValue = (val) => {
    dispatch(
      updateSelectedField({
        ...field,
        defaultValue: val as string[],
      }),
    );
  };

  const handleGoUp = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx > 0) {
      const tempOptions = [...options];
      const tempObj = tempOptions[idx - 1];
      tempOptions[idx - 1] = tempOptions[idx];
      tempOptions[idx] = tempObj;
      setOptions(tempOptions);
      dispatch(
        updateSelectedField({
          ...field,
          options: tempOptions,
        }),
      );
      e.preventDefault();
    }
  };
  const handleGoDown = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx < options.length - 1) {
      const tempOptions = [...options];
      const tempObj = tempOptions[idx + 1];
      tempOptions[idx + 1] = tempOptions[idx];
      tempOptions[idx] = tempObj;
      setOptions(tempOptions);
      dispatch(
        updateSelectedField({
          ...field,
          options: tempOptions,
        }),
      );
      e.preventDefault();
    }
  };

  return (
    <Form>
      <Row className="d-flex justify-content-center align-items-center">
        <table className="w-100 mb-4">
          {options.length > 0 && (
            <thead>
              <tr>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.order" />
                  </FormLabel>
                </th>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.options" />
                  </FormLabel>
                </th>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.hide" />
                  </FormLabel>
                </th>
                {field.type == FieldType.OptionList && (
                  <th className="text-center">
                    <FormLabel>
                      <FormattedMessage id="adminProperties.settings.options.default" />
                    </FormLabel>
                  </th>
                )}
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>
            {options?.map((option, index) => {
              return (
                <tr className="option-row" key={option.value}>
                  <td className="d-flex justify-content-center align-items-center">
                    <button
                      className="btn btn-primary move-up"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoUp(e, index)}
                    >
                      <span className="mdi mdi-arrow-up" />
                    </button>
                    <button
                      className=" btn btn-primary move-down"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoDown(e, index)}
                    >
                      <span className="mdi mdi-arrow-down" />
                    </button>
                  </td>
                  <td className="pb-2">
                    <CultureTextInput
                      id={`field.${field.id}.option.${index}.name`}
                      localizations={localizations}
                      culture={selectedLanguage}
                      value={option.text}
                      onChange={(e) => updateOption({ ...option, text: e }, index)}
                    />
                  </td>
                  <td className="pb-2 text-center align-top">
                    <CheckboxInput
                      id={`${option.value}-hidden`}
                      value={option.isHidden}
                      onChange={() => updateOption({ ...option, isHidden: !option.isHidden }, index)}
                    />
                  </td>
                  {field.type == FieldType.OptionList && (
                    <td className="pb-2 text-center align-top">
                      <CheckboxInput
                        id={option.value}
                        value={isDefaultValue(option.value)}
                        onChange={() => toggleDefault(option.value)}
                      />
                    </td>
                  )}
                  <td className="pb-2">
                    <div
                      className="delete-btn ms-2"
                      onClick={() => {
                        const newOptions = filter(options, (item) => item !== option);
                        setOptions(newOptions);
                        dispatch(updateSelectedField({ ...field, options: newOptions }));
                      }}
                    >
                      <span className="mdi mdi-close"></span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button
          className="w-50 btn btn-primary btn-block btn-lg"
          onClick={(e) => {
            const newOption = new Option(localizations);
            const newOptions = [...options, { ...newOption }];
            setOptions(newOptions);
            dispatch(updateSelectedField({ ...field, options: newOptions }));
            e.preventDefault();
          }}
        >
          {intl.formatMessage({ id: 'formAdmin.addButton' })}
        </button>
      </Row>
    </Form>
  );
};

export default OptionListOptions;
